'use client'
import { useEffect, useRef, useState, useTransition } from 'react'
import { logout } from '@/app/actions/logout'
import { updatePreferences } from '@/app/actions/account'
import { useNavUI } from '@/lib/navContext'
import { buildNavRegistry, EXTRAS_NAV_ITEMS, type NavItem } from '@/lib/navItems'
import { isTypingTarget } from '@/lib/keyboardUtils'
import { isGameActive } from '@/lib/gameFocus'
import type { LeaderboardList, CasinoList, UserPreferences, Market, EventSummary, PinnedMarketEntry } from '@/lib/api'
import Link from 'next/dist/client/link'
import { usePathname, useSearchParams, useRouter } from 'next/navigation'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Props {
  username: string
  leaderboardList: LeaderboardList
  casinoList: CasinoList
  initialPrefs: UserPreferences
  markets: Market[]
  events: EventSummary[]
}

export const SECTIONS = [
  { key: 'pinned',       icon: '📌', label: 'Pinned'       },
  { key: 'casino',       icon: '🎲', label: 'Casino'       },
  { key: 'leaderboard',  icon: '🏆', label: 'Leaderboard'  },
  { key: 'collectibles', icon: '🎴', label: 'Collectibles' },
  { key: 'extras',       icon: '⚙️', label: 'Extras'       },
] as const
type SectionKey = typeof SECTIONS[number]['key']

const SECTION_KEYS: Record<string, SectionKey> = {
  '1': 'pinned', '2': 'casino', '3': 'leaderboard', '4': 'collectibles', '5': 'extras',
}

export function getRouteSection(pathname: string): SectionKey {
  if (pathname.startsWith('/casino')) return 'casino'
  if (pathname.startsWith('/leaderboard')) return 'leaderboard'
  if (pathname.startsWith('/collectibles')) return 'collectibles'
  if (/^\/(profile|settings|admin|loans|unemployment|fantasy-dungeon-league|cheese-singularity)/.test(pathname)) return 'extras'
  return 'pinned'
}

function PinToggle({ pinned, onToggle }: { pinned: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={e => { e.preventDefault(); e.stopPropagation(); onToggle() }}
      aria-label={pinned ? 'Unpin' : 'Pin'}
      className={`ml-auto shrink-0 p-1 rounded transition-colors ${pinned ? 'text-indigo-500 hover:text-indigo-400' : 'text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-500'}`}
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill={pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
      </svg>
    </button>
  )
}

function MobileSection({ label, open, onToggle, children }: { label: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors rounded-lg ${open ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
      >
        <svg className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
        {label}
      </button>
      {open && <div className="pl-2">{children}</div>}
    </div>
  )
}

function SortablePinnedItem({ item, onPin, onClick, editingPins, keyboardSelected = false }: { item: NavItem; onPin: () => void; onClick: () => void; editingPins: boolean; keyboardSelected?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.key })
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className="flex items-center rounded-lg"
    >
      {editingPins && (
        <button
          {...listeners}
          tabIndex={-1}
          aria-label="Drag to reorder"
          className="p-1 pl-2 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 cursor-grab active:cursor-grabbing touch-none shrink-0"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="5" cy="4" r="1.2"/><circle cx="11" cy="4" r="1.2"/>
            <circle cx="5" cy="8" r="1.2"/><circle cx="11" cy="8" r="1.2"/>
            <circle cx="5" cy="12" r="1.2"/><circle cx="11" cy="12" r="1.2"/>
          </svg>
        </button>
      )}
      <Link
        href={item.route}
        onClick={onClick}
        className={`flex-1 flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
          keyboardSelected
            ? 'bg-indigo-50/60 dark:bg-indigo-900/10 text-indigo-600 dark:text-indigo-400 ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
      >
        <span className="text-base leading-none">{item.emoji}</span>
        <span className="flex-1 truncate">{item.alt_name ?? item.name}</span>
        {editingPins && <PinToggle pinned onToggle={onPin} />}
      </Link>
    </div>
  )
}

function SidebarItem({ item, pinned, onPin, onClick, editingPins, keyboardSelected = false }: { item: NavItem; pinned: boolean; onPin: () => void; onClick: () => void; editingPins: boolean; keyboardSelected?: boolean }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const currentRoute = searchParams.size > 0 ? `${pathname}?${searchParams}` : pathname
  const isActive = currentRoute === item.route
  return (
    <Link
      href={item.route}
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
        isActive
          ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-medium'
          : keyboardSelected
          ? 'bg-indigo-50/60 dark:bg-indigo-900/10 text-indigo-600 dark:text-indigo-400 ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
      }`}
    >
      <span className="text-base leading-none">{item.emoji}</span>
      <span className="flex-1 truncate">{item.alt_name ?? item.name}</span>
      {editingPins && <PinToggle pinned={pinned} onToggle={onPin} />}
    </Link>
  )
}

export default function Sidebar({ username, leaderboardList, casinoList, initialPrefs, markets, events }: Props) {
  const { sidebarOpen, openSidebar, closeSidebar, pinnedMarkets, setPinnedMarkets } = useNavUI()
  const [prefs, setPrefs] = useState<UserPreferences>(initialPrefs)
  const [, startTransition] = useTransition()
  const router = useRouter()
  const [editingPins, setEditingPins] = useState(false)
  const [hoveredSection, setHoveredSection] = useState<string | null>(null)
  const [keyboardSection, setKeyboardSection] = useState<SectionKey | null>(null)
  // The section the user clicked open in the activity bar — chooses which section the panel
  // shows and keeps it open as a floating overlay (no layout shift). Not persisted, so the
  // panel never auto-opens on load and nothing has to stay consistent across navigations.
  const [selectedSection, setSelectedSection] = useState<SectionKey | null>(null)
  const [keyboardItemIdx, setKeyboardItemIdx] = useState(-1)
  // Always-current values for the stable keyboard handler
  const kbStateRef = useRef({ panelOpen: false, sectionRoutes: [] as string[], routeSection: 'pinned' as SectionKey, keyboardItemIdx: -1 })

  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem('weeabets.sidebar_sections')
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set(['casino', 'leaderboard'])
    } catch { return new Set(['casino', 'leaderboard']) }
  })

  const pathname = usePathname()
  const routeSection = getRouteSection(pathname)
  // Precedence: hover > keyboard 1-5 > activity-bar click (selectedSection) > current route
  const activeSection: SectionKey = (hoveredSection ?? keyboardSection ?? selectedSection ?? routeSection) as SectionKey
  const panelOpen = hoveredSection !== null || keyboardSection !== null || selectedSection !== null

  const registry = buildNavRegistry(leaderboardList, casinoList, username)
  const pinnedItems = prefs.pinned_nav.map(k => registry.get(k)).filter((item): item is NavItem => !!item)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeSidebar()
        setKeyboardSection(null)
        setKeyboardItemIdx(-1)
        setSelectedSection(null)
        return
      }
      // A game (e.g. Tetris) currently owns the keyboard — yield everything below to it. This
      // is what makes it safe to bind Arrow keys here: see lib/gameFocus.ts (game:focus/blur).
      if (isGameActive()) return
      if (isTypingTarget(e)) return
      if (e.metaKey || e.ctrlKey) {
        if (e.key === '\\') {
          e.preventDefault()
          if (sidebarOpen) closeSidebar(); else openSidebar()
        } else if (e.key === 'b' || e.key === 'B') {
          e.preventDefault()
          const { panelOpen: open, routeSection: rs } = kbStateRef.current
          if (open) {
            setKeyboardSection(null)
            setSelectedSection(null)
            setKeyboardItemIdx(-1)
          } else {
            setKeyboardSection(rs)
          }
        }
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // Only steal Arrow keys to navigate sidebar entries when the panel is already open —
        // otherwise let the browser use them for its default behavior (e.g. page scroll).
        const { panelOpen: open, sectionRoutes: routes } = kbStateRef.current
        if (!open || !routes.length) return
        e.preventDefault()
        setKeyboardItemIdx(prev =>
          e.key === 'ArrowDown' ? (prev + 1) % routes.length : (prev <= 0 ? routes.length - 1 : prev - 1)
        )
        return
      }
      if (e.key === 'Enter') {
        const { sectionRoutes: routes, keyboardItemIdx: idx } = kbStateRef.current
        if (idx >= 0 && idx < routes.length) {
          e.preventDefault()
          router.push(routes[idx])
          setKeyboardItemIdx(-1)
          setKeyboardSection(null)
        }
        return
      }
      const s = SECTION_KEYS[e.key]
      if (s) {
        e.preventDefault()
        setKeyboardSection(prev => prev === s ? null : s)
        setKeyboardItemIdx(-1)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [sidebarOpen, closeSidebar, openSidebar, router])

  // Reset item cursor whenever the visible section changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset cursor when the active section changes
    setKeyboardItemIdx(-1)
  }, [activeSection])

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = prefs.pinned_nav.indexOf(active.id as string)
    const newIndex = prefs.pinned_nav.indexOf(over.id as string)
    const next = arrayMove(prefs.pinned_nav, oldIndex, newIndex)
    setPrefs(prev => ({ ...prev, pinned_nav: next }))
    startTransition(() => { updatePreferences({ pinned_nav: next }) })
  }

  // Clicking a section icon just opens the panel to that section (toggle closed if it's already
  // the open one). It never pins/unpins — pinning (the layout shift) is the pin button's job.
  function handleSectionClick(s: SectionKey) {
    setSelectedSection(prev => (prev === s ? null : s))
  }

  function handleNavClick() {
    setSelectedSection(null)
    if (sidebarOpen) closeSidebar()
  }

  function isPinned(key: string) { return prefs.pinned_nav.includes(key) }

  function togglePin(key: string) {
    const next = isPinned(key) ? prefs.pinned_nav.filter(k => k !== key) : [...prefs.pinned_nav, key]
    setPrefs(prev => ({ ...prev, pinned_nav: next }))
    startTransition(() => { updatePreferences({ pinned_nav: next }) })
  }

  function toggleMarketPin(entry: PinnedMarketEntry) {
    const already = pinnedMarkets.some(p => p.type === entry.type && p.id === entry.id)
    const next = already ? pinnedMarkets.filter(p => !(p.type === entry.type && p.id === entry.id)) : [...pinnedMarkets, entry]
    setPinnedMarkets(next)
    startTransition(() => { updatePreferences({ pinned_markets: next }) })
  }

  function toggleMobileAccordion(key: string) {
    setOpenSections(prev => {
      const next = new Set(prev)
      if (next.has(key)) { next.delete(key) } else { next.add(key) }
      try { sessionStorage.setItem('weeabets.sidebar_sections', JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }

  // ── Item lists ──

  const casinoItems = casinoList.games.map(game => ({
    key: `casino:${game.key}`,
    item: { key: `casino:${game.key}`, emoji: game.emoji, name: game.name, alt_name: game.alt_name, route: game.route } as NavItem,
  }))

  const leaderboardItems = leaderboardList.boards.map(board => ({
    key: `leaderboard:${board.key}`,
    item: { key: `leaderboard:${board.key}`, emoji: board.emoji, name: board.name, alt_name: board.alt_name, route: board.route } as NavItem,
  }))
  const favLeaderboardItems = leaderboardItems.filter(x => prefs.favourite_leaderboards.includes(x.key.replace('leaderboard:', '')))
  const restLeaderboardItems = leaderboardItems.filter(x => !prefs.favourite_leaderboards.includes(x.key.replace('leaderboard:', '')))

  const collectiblesItems = [
    { key: 'nav:collectibles:dinos', emoji: '🦖', name: 'Dino Garage', route: '/collectibles?tab=dinos' },
    { key: 'nav:collectibles:cards', emoji: '🃏', name: 'Card Binder',  route: '/collectibles?tab=binder' },
  ]

  const extrasItems = EXTRAS_NAV_ITEMS

  // Flat route list for the active section — drives arrow key navigation
  const sectionRoutes: string[] = (() => {
    switch (activeSection) {
      case 'pinned':
        return [
          ...pinnedItems.map(i => i.route),
          ...pinnedMarkets.map(e => e.type === 'market' ? `/markets/${e.id}` : `/events/${e.id}`),
        ]
      case 'casino':       return casinoItems.map(x => x.item.route)
      case 'leaderboard':  return [...favLeaderboardItems.map(x => x.item.route), ...restLeaderboardItems.map(x => x.item.route)]
      case 'collectibles': return collectiblesItems.map(x => x.route)
      case 'extras':       return extrasItems.map(x => x.route)
    }
  })()
  const keyboardSelectedRoute = keyboardItemIdx >= 0 ? (sectionRoutes[keyboardItemIdx] ?? null) : null
  // Keep ref in sync so the stable keyboard handler always sees latest values
  // eslint-disable-next-line react-hooks/refs -- intentional: update mutable ref during render to avoid stale closure in keyboard handler
  kbStateRef.current = { panelOpen, sectionRoutes, routeSection: routeSection as SectionKey, keyboardItemIdx }

  // ── Render helpers ──

  function renderPinnedDnd() {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={prefs.pinned_nav} strategy={verticalListSortingStrategy}>
          {pinnedItems.map(item => (
            <SortablePinnedItem key={item.key} item={item} onClick={handleNavClick} onPin={() => togglePin(item.key)} editingPins={editingPins} keyboardSelected={keyboardSelectedRoute === item.route} />
          ))}
        </SortableContext>
      </DndContext>
    )
  }

  function renderPinnedMarkets() {
    return pinnedMarkets.map(entry => {
      if (entry.type === 'market') {
        const m = markets.find(x => x.id === entry.id)
        const deleted = !m
        const resolved = m?.status === 'resolved'
        const locked = m?.status === 'locked'
        const dimmed = deleted || resolved
        const kbSelected = keyboardSelectedRoute === `/markets/${entry.id}`
        return (
          <div key={`pm-${entry.id}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${dimmed ? 'opacity-50' : kbSelected ? 'bg-indigo-50/60 dark:bg-indigo-900/10 ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'} transition-colors`}>
            <Link href={deleted ? '#' : `/markets/${entry.id}`} onClick={deleted ? undefined : handleNavClick} className="flex-1 flex items-center gap-2 min-w-0">
              <span className="text-base leading-none shrink-0">📊</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-gray-700 dark:text-gray-300 truncate leading-snug">
                  {deleted ? `Market #${entry.id}` : m.question}
                </span>
                <span className={`text-[10px] font-semibold ${deleted ? 'text-gray-400' : resolved ? 'text-gray-400' : locked ? 'text-amber-500' : 'text-indigo-500'}`}>
                  {deleted ? 'Deleted' : resolved
                    ? `Resolved ${m.outcome === 'yes' ? (m.yes_label ?? 'YES').toUpperCase() : (m.no_label ?? 'NO').toUpperCase()}`
                    : locked ? 'Locked' : `${Math.round(m.yes_prob * 100)}% ${(m.yes_label ?? 'YES').toUpperCase()}`}
                </span>
              </span>
            </Link>
            {editingPins && <PinToggle pinned onToggle={() => toggleMarketPin(entry)} />}
          </div>
        )
      } else {
        const ev = events.find(x => x.id === entry.id)
        const deleted = !ev
        const resolved = ev?.status === 'resolved'
        const dimmed = deleted || resolved
        const openCount = ev ? ev.markets.filter(m => m.yes_prob > 0).length : 0
        const kbSelectedEv = keyboardSelectedRoute === `/events/${entry.id}`
        return (
          <div key={`pe-${entry.id}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${dimmed ? 'opacity-50' : kbSelectedEv ? 'bg-indigo-50/60 dark:bg-indigo-900/10 ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'} transition-colors`}>
            <Link href={deleted ? '#' : `/events/${entry.id}`} onClick={deleted ? undefined : handleNavClick} className="flex-1 flex items-center gap-2 min-w-0">
              <span className="text-base leading-none shrink-0">🎯</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-gray-700 dark:text-gray-300 truncate leading-snug">
                  {deleted ? `Event #${entry.id}` : ev.question}
                </span>
                <span className={`text-[10px] font-semibold ${deleted || resolved ? 'text-gray-400' : 'text-indigo-500'}`}>
                  {deleted ? 'Deleted' : resolved ? 'Resolved' : `${openCount} market${openCount !== 1 ? 's' : ''}`}
                </span>
              </span>
            </Link>
            {editingPins && <PinToggle pinned onToggle={() => toggleMarketPin(entry)} />}
          </div>
        )
      }
    })
  }

  function renderSectionContent(key: SectionKey) {
    switch (key) {
      case 'pinned':
        if (pinnedItems.length === 0 && pinnedMarkets.length === 0) {
          return <p className="px-3 py-6 text-xs text-center text-gray-400 dark:text-gray-600">Pin items using the ★ button</p>
        }
        return <>{renderPinnedDnd()}{renderPinnedMarkets()}</>
      case 'casino':
        return <>{casinoItems.map(({ key: k, item }) => <SidebarItem key={k} item={item} pinned={isPinned(k)} onClick={handleNavClick} onPin={() => togglePin(k)} editingPins={editingPins} keyboardSelected={keyboardSelectedRoute === item.route} />)}</>
      case 'leaderboard':
        return (
          <>
            {favLeaderboardItems.map(({ key: k, item }) => (
              <SidebarItem key={k} item={item} pinned={isPinned(k)} onClick={handleNavClick} onPin={() => togglePin(k)} editingPins={editingPins} keyboardSelected={keyboardSelectedRoute === item.route} />
            ))}
            {favLeaderboardItems.length > 0 && restLeaderboardItems.length > 0 && (
              <div className="my-1 border-t border-gray-100 dark:border-gray-800" />
            )}
            {restLeaderboardItems.map(({ key: k, item }) => (
              <SidebarItem key={k} item={item} pinned={isPinned(k)} onClick={handleNavClick} onPin={() => togglePin(k)} editingPins={editingPins} keyboardSelected={keyboardSelectedRoute === item.route} />
            ))}
          </>
        )
      case 'collectibles':
        return <>{collectiblesItems.map(it => <SidebarItem key={it.key} item={it as NavItem} pinned={isPinned(it.key)} onClick={handleNavClick} onPin={() => togglePin(it.key)} editingPins={editingPins} keyboardSelected={keyboardSelectedRoute === it.route} />)}</>
      case 'extras':
        return <>{extrasItems.map(it => <SidebarItem key={it.key} item={it as NavItem} pinned={isPinned(it.key)} onClick={handleNavClick} onPin={() => togglePin(it.key)} editingPins={editingPins} keyboardSelected={keyboardSelectedRoute === it.route} />)}</>
    }
  }

  const activeLabel = SECTIONS.find(s => s.key === activeSection)?.label ?? ''

  return (
    <>
      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <>
          <div onClick={closeSidebar} className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden" aria-hidden="true" />
          <div className="fixed top-0 left-0 h-full w-72 z-50 bg-white dark:bg-gray-900 flex flex-col overflow-hidden shadow-2xl md:hidden">
            <div className="flex items-center gap-1 px-3 py-3 border-b border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setEditingPins(e => !e)}
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors mr-auto ${editingPins ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
              >
                {editingPins ? 'Done' : 'Edit Pins'}
              </button>
              <button onClick={closeSidebar} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" aria-label="Close sidebar">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2 space-y-1 px-2">
              {(pinnedItems.length > 0 || pinnedMarkets.length > 0) && (
                <MobileSection label="Pinned" open={openSections.has('pinned')} onToggle={() => toggleMobileAccordion('pinned')}>
                  {renderPinnedDnd()}
                  {renderPinnedMarkets()}
                </MobileSection>
              )}
              <MobileSection label="Casino" open={openSections.has('casino')} onToggle={() => toggleMobileAccordion('casino')}>
                {casinoItems.map(({ key: k, item }) => <SidebarItem key={k} item={item} pinned={isPinned(k)} onClick={handleNavClick} onPin={() => togglePin(k)} editingPins={editingPins} />)}
              </MobileSection>
              <MobileSection label="Leaderboard" open={openSections.has('leaderboard')} onToggle={() => toggleMobileAccordion('leaderboard')}>
                {leaderboardItems.map(({ key: k, item }) => <SidebarItem key={k} item={item} pinned={isPinned(k)} onClick={handleNavClick} onPin={() => togglePin(k)} editingPins={editingPins} />)}
              </MobileSection>
              <MobileSection label="Collectibles" open={openSections.has('collectibles')} onToggle={() => toggleMobileAccordion('collectibles')}>
                {collectiblesItems.map(it => <SidebarItem key={it.key} item={it as NavItem} pinned={isPinned(it.key)} onClick={handleNavClick} onPin={() => togglePin(it.key)} editingPins={editingPins} />)}
              </MobileSection>
              <MobileSection label="Extras" open={openSections.has('extras')} onToggle={() => toggleMobileAccordion('extras')}>
                {extrasItems.map(it => <SidebarItem key={it.key} item={it as NavItem} pinned={isPinned(it.key)} onClick={handleNavClick} onPin={() => togglePin(it.key)} editingPins={editingPins} />)}
              </MobileSection>
              <div className="pt-1 border-t border-gray-100 dark:border-gray-800 mt-1">
                <form action={logout}>
                  <button type="submit" className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                    <span className="text-base">🚪</span>
                    <span>Logout</span>
                  </button>
                </form>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* ── Desktop: activity bar + side panel ── */}
      <div
        className="hidden md:flex fixed top-0 left-0 h-full z-50"
        onMouseLeave={() => setHoveredSection(null)}
      >
        {/* Activity bar — always w-12, never changes size */}
        <div className="w-12 h-full bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col items-center py-3 gap-0.5">
          {SECTIONS.map(s => {
            const isActive = activeSection === s.key
            return (
              <button
                key={s.key}
                onClick={() => handleSectionClick(s.key)}
                onMouseEnter={() => setHoveredSection(s.key)}
                title={s.label}
                aria-label={s.label}
                className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg ${
                  isActive
                    ? 'bg-white dark:bg-gray-800 shadow-sm'
                    : 'text-gray-400 dark:text-gray-600 hover:bg-white dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {s.icon}
              </button>
            )
          })}

          {/* Logout pinned to bottom */}
          <div className="mt-auto">
            <form action={logout}>
              <button
                type="submit"
                title="Logout"
                aria-label="Logout"
                onMouseEnter={() => setHoveredSection(null)}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-lg text-gray-400 dark:text-gray-600 hover:bg-white dark:hover:bg-gray-800 hover:text-red-500 transition-all"
              >
                🚪
              </button>
            </form>
          </div>
        </div>

        {/* Side panel — floats over content on hover or click (never shifts the layout) */}
        {panelOpen && (
          <div className="w-60 h-full bg-white dark:bg-gray-900 flex flex-col shadow-xl">
            {/* Panel header */}
            <div className="flex items-center gap-1 px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
              <span className="flex-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {activeLabel}
              </span>
              {activeSection === 'pinned' && (
                <button
                  onClick={() => setEditingPins(e => !e)}
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors ${editingPins ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                >
                  {editingPins ? 'Done' : 'Edit'}
                </button>
              )}
            </div>

            {/* Panel items */}
            <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
              {renderSectionContent(activeSection)}
            </nav>
          </div>
        )}
      </div>
    </>
  )
}
